import type {
  Design,
  FengShuiConsultation,
  FengShuiRanking,
  Furniture,
  Opening,
  Person,
  Room,
  Unit,
  WallMount,
} from "./types";

/** Arrow nudges and stepper clicks share one undo step after this much quiet. */
export const NUDGE_GESTURE_MS = 400;

export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

type Identified = { id: string };

export type ItemPatchOp<T extends Identified> =
  | { op: "insert"; item: T }
  | { op: "remove"; id: string; item: T }
  | { op: "update"; id: string; fields: Partial<T> };

export interface HistoryPatch {
  furniture?: ItemPatchOp<Furniture>[];
  mounts?: ItemPatchOp<WallMount>[];
  openings?: ItemPatchOp<Opening>[];
  furnitureOrder?: string[];
  room?: Room;
  person?: Person;
  unit?: Unit;
  consultations?: FengShuiConsultation[];
  rankings?: FengShuiRanking[];
}

export interface HistoryEntry {
  undo: HistoryPatch;
  redo: HistoryPatch;
}

export function isHistoryPatchEmpty(patch: HistoryPatch): boolean {
  return (
    (patch.furniture == null || patch.furniture.length === 0) &&
    (patch.mounts == null || patch.mounts.length === 0) &&
    (patch.openings == null || patch.openings.length === 0) &&
    patch.furnitureOrder == null &&
    patch.room == null &&
    patch.person == null &&
    patch.unit == null &&
    patch.consultations == null &&
    patch.rankings == null
  );
}

/** Field-level diff of two designs. `null` when nothing changed. */
export function diffDesign(prev: Design, next: Design): HistoryEntry | null {
  const undo: HistoryPatch = {};
  const redo: HistoryPatch = {};

  diffCollection("furniture", prev.furniture, next.furniture, undo, redo);
  diffCollection("mounts", prev.mounts, next.mounts, undo, redo);
  diffCollection("openings", prev.openings ?? [], next.openings ?? [], undo, redo);

  const prevOrder = prev.furniture.map((f) => f.id);
  const nextOrder = next.furniture.map((f) => f.id);
  if (!sameJson(prevOrder, nextOrder)) {
    undo.furnitureOrder = prevOrder.slice();
    redo.furnitureOrder = nextOrder.slice();
  }

  if (!sameJson(prev.room, next.room)) {
    undo.room = structuredClone(prev.room);
    redo.room = structuredClone(next.room);
  }
  if (!sameJson(prev.person, next.person)) {
    undo.person = structuredClone(prev.person);
    redo.person = structuredClone(next.person);
  }
  if (prev.unit !== next.unit) {
    undo.unit = prev.unit;
    redo.unit = next.unit;
  }

  const prevConsultations = prev.consultations ?? [];
  const nextConsultations = next.consultations ?? [];
  if (!sameJson(prevConsultations, nextConsultations)) {
    undo.consultations = structuredClone(prevConsultations);
    redo.consultations = structuredClone(nextConsultations);
  }

  const prevRankings = prev.rankings ?? [];
  const nextRankings = next.rankings ?? [];
  if (!sameJson(prevRankings, nextRankings)) {
    undo.rankings = structuredClone(prevRankings);
    redo.rankings = structuredClone(nextRankings);
  }

  if (isHistoryPatchEmpty(undo) && isHistoryPatchEmpty(redo)) return null;
  return { undo, redo };
}

/**
 * Apply a patch onto `design` (the current document, not a historical
 * snapshot). Missing items are skipped so a collaborator delete cannot
 * resurrect a field-level undo as a crash.
 */
export function applyHistoryPatch(design: Design, patch: HistoryPatch): Design {
  let furniture = applyCollection(design.furniture, patch.furniture);
  furniture = applyOrder(furniture, patch.furnitureOrder);
  const mounts = applyCollection(design.mounts, patch.mounts);
  const openings = applyCollection(design.openings ?? [], patch.openings);

  const next: Design = {
    ...design,
    furniture,
    mounts,
    openings,
  };
  if (patch.room) next.room = structuredClone(patch.room);
  if (patch.person) next.person = structuredClone(patch.person);
  if (patch.unit) next.unit = patch.unit;
  if (patch.consultations) next.consultations = structuredClone(patch.consultations);
  if (patch.rankings) next.rankings = structuredClone(patch.rankings);
  return next;
}

/**
 * Rebase an in-flight gesture onto a remotely-arrived design: collaborator
 * changes land in both the baseline and the current document; fields this
 * user already changed stay dirty so the patch at gesture-end is local-only.
 */
export function rebaseGestureDesign(
  baseline: Design,
  current: Design,
  incoming: Design
): { baseline: Design; current: Design } {
  const local = diffDesign(baseline, current);
  const nextBaseline = structuredClone(incoming);
  if (!local) {
    return { baseline: nextBaseline, current: structuredClone(incoming) };
  }
  return {
    baseline: nextBaseline,
    current: applyHistoryPatch(incoming, local.redo),
  };
}

function diffCollection<T extends Identified>(
  key: "furniture" | "mounts" | "openings",
  prevList: T[],
  nextList: T[],
  undo: HistoryPatch,
  redo: HistoryPatch
): void {
  const prev = keyById(prevList);
  const next = keyById(nextList);
  const undoOps: ItemPatchOp<T>[] = [];
  const redoOps: ItemPatchOp<T>[] = [];

  for (const id of Object.keys(next)) {
    const a = prev[id];
    const b = next[id];
    if (!a) {
      redoOps.push({ op: "insert", item: structuredClone(b) });
      undoOps.push({ op: "remove", id, item: structuredClone(b) });
      continue;
    }
    const changed = diffItemFields(a, b);
    if (!changed) continue;
    redoOps.push({ op: "update", id, fields: changed.after });
    undoOps.push({ op: "update", id, fields: changed.before });
  }
  for (const id of Object.keys(prev)) {
    if (next[id]) continue;
    const item = structuredClone(prev[id]);
    redoOps.push({ op: "remove", id, item });
    undoOps.push({ op: "insert", item });
  }

  if (redoOps.length === 0) return;
  (undo as Record<string, unknown>)[key] = undoOps;
  (redo as Record<string, unknown>)[key] = redoOps;
}

function diffItemFields<T extends Identified>(
  a: T,
  b: T
): { before: Partial<T>; after: Partial<T> } | null {
  const aRec = a as unknown as Record<string, unknown>;
  const bRec = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(aRec), ...Object.keys(bRec)]);
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  let changed = false;
  for (const key of keys) {
    if (sameJson(aRec[key], bRec[key])) continue;
    before[key] = aRec[key];
    after[key] = bRec[key];
    changed = true;
  }
  if (!changed) return null;
  return { before: before as Partial<T>, after: after as Partial<T> };
}

function applyCollection<T extends Identified>(
  current: T[],
  ops: ItemPatchOp<T>[] | undefined
): T[] {
  if (!ops || ops.length === 0) return current;
  let next = current.slice();
  for (const op of ops) {
    if (op.op === "remove") {
      next = next.filter((item) => item.id !== op.id);
      continue;
    }
    if (op.op === "insert") {
      if (next.some((item) => item.id === op.item.id)) continue;
      next.push(structuredClone(op.item));
      continue;
    }
    next = next.map((item) =>
      item.id === op.id ? applyFields(item, op.fields) : item
    );
  }
  return next;
}

function applyFields<T extends Identified>(item: T, fields: Partial<T>): T {
  // Mutate a plain record. Writing `next[key]` on `T` is illegal under
  // TypeScript 6 (TS2862): a generic is only indexable for reading.
  const next: Record<string, unknown> = { ...item };
  const rec = fields as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    const value = rec[key];
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next as T;
}

function applyOrder<T extends Identified>(list: T[], order?: string[]): T[] {
  if (!order) return list;
  const byId = new Map(list.map((item) => [item.id, item]));
  const ordered: T[] = [];
  for (const id of order) {
    const item = byId.get(id);
    if (!item) continue;
    ordered.push(item);
    byId.delete(id);
  }
  for (const item of byId.values()) ordered.push(item);
  return ordered;
}

function keyById<T extends Identified>(list: T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of list) out[item.id] = item;
  return out;
}
